/**
 * WebSocket notification client for real-time system alerts.
 *
 * Connects to /ws/notifications with the current JWT token and dispatches
 * incoming payloads to registered subscribers.
 */
import { store } from '../store';

const WS_BASE = `${typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${typeof window !== 'undefined' ? window.location.host : 'localhost:8000'}`;

type NotificationHandler = (payload: Record<string, unknown>) => void;

class NotificationService {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private handlers: Set<NotificationHandler> = new Set();
  private connectAttempts = 0;
  private manualDisconnect = false;

  connect() {
    this.manualDisconnect = false;
    if (this.ws?.readyState === WebSocket.OPEN || this.ws?.readyState === WebSocket.CONNECTING) {
      return;
    }
    const state = store.getState();
    const token = state.auth?.token;
    if (!token) {
      // Defer until authenticated.
      return;
    }
    const url = `${WS_BASE}/ws/notifications?token=${encodeURIComponent(token)}`;
    try {
      this.ws = new WebSocket(url);
      this.ws.onopen = () => {
        this.connectAttempts = 0;
      };
      this.ws.onmessage = (event) => {
        try {
          const payload = JSON.parse(event.data);
          if (payload.type === 'ping') return;
          this.handlers.forEach((h) => h(payload));
        } catch {
          // Ignore malformed messages.
        }
      };
      this.ws.onclose = () => {
        this.scheduleReconnect();
      };
      this.ws.onerror = () => {
        this.ws?.close();
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  disconnect() {
    this.manualDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
  }

  subscribe(handler: NotificationHandler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private scheduleReconnect() {
    if (this.manualDisconnect || this.reconnectTimer) return;
    this.connectAttempts += 1;
    const delay = Math.min(1000 * 2 ** this.connectAttempts, 30000);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

export const notificationService = new NotificationService();
